import { API_URL } from "env";
import { toast } from "react-toastify";

interface OrderData {
    list_products: {
        id: number;
        product_category_id: number;
        price: number;
        quantity: number;
    }[];
    address: string;
    phone: string;
    fullname: string;
    customer_id: number;
    voucher_code?: string;

    note?: string;
    payment: 1 | 2; // 1: Cash on Delivery, 2: Bank Transfer
}

interface OrderResponse {
    order_id: string;
    status: boolean;
    msg: string | null;
}

interface OrderV3Data {
    list_products: string;
    booking_ids?: number[];
    redeem_gifts?: string; // JSON stringified array of gift IDs {gift_id: number, qty: number}[]
    payment?: 1 | 2 | 3;
    address?: string;
    phone?: string;
    fullname?: string;
    note?: string;
}

interface OrderV3Response {
    status: boolean;
    data?: {
        id: number;
        [key: string]: unknown;
    };
    msg?: string | null;
}

interface OrderListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: {
            id: number;
            code: string;
            customer_id: number;
            fullname: string;
            phone: string;
            total: number;
            status: number;
            payment_status: number;
            created_at: string;
            address: string;
            order_product: {
                id: number;
                order_id: number;
                slug_product: string;
                quantity_product: number;
                name_product: string;
                avatar_product: string;
                price_product: number;
                product_id?: number;
            }[];
        }[];
    };
    msg: string;
}

interface OrderDetailResponse {
    status: boolean;
    data: {
        id: number;
        customer_id: number;
        code: string;
        total: number;
        total_discount: number | null;
        voucher_id?: number | null;
        fullname: string;
        phone: string;
        address: string;
        note?: string;
        status?: number;
        payment?: number;
        payment_status?: number;
        created_at?: string;
        updated_at?: string;
        actual_total ?: number;
        actual_total_discount ?: number;
        history: {
            id: number;
            order_id: number;
            created_by: number;
            status: string;
            note: string;
            created_at: string;
            updated_at: string;
        }[];
        order_product: {
            id: number;
            status: number | null;
            discount_price: number | null;
            order_id: number;
            quantity_product: number;
            name_product: string;
            slug_product: string;
            avatar_product: string;
            price_product: number;
            product_id?: number;
            point_product?: number;
            actual_quantity?: number;

        }[];
        redeemed_gifts?: {
            id: number;
            order_id: number;
            gift_id: number;
            name: string;
            avatar: string;
            point: number;
            status: 'PENDING' | 'DONE' | 'CANCELLED' | string;
            product?: {
                avatar: string;
                id: number;
                name: string;
                slug: string;
            }
        }[];
        voucher?: {
            id: number;
            name: string;
            code: string;
            type: 'fixed' | 'percent' | 'ship' | 'gift';
            value: string | number;
            cap_amount?: string | number | null;
            global_quota?: number | null;
            start_date?: string | null;
            end_date?: string | null;
        } | null;
        pricing?: {
            original_items_amount: number;
            total_discount: number;
            final_items_amount: number;
            final_total_amount: number;
            actual_original_amount ?: number;
            actual_total_discount ?: number;
            actual_final_amount ?: number;
            voucher_breakdown?: {
                code: string;
                template_name: string;
                discount_applied: number;
                applied_to: 'order' | 'shipping';
                type: 'fixed' | 'percent' | 'ship' | 'gift';
            }[];
        };
    };
    msg: string | null;
}

type RawObject = Record<string, any>;

function toNumber(value: unknown, defaultValue = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : defaultValue;
}

function normalizeOrderListResponse(raw: RawObject): OrderListResponse {
    const rawData: RawObject = raw?.data && typeof raw.data === 'object' ? raw.data : {};
    const rawItems = Array.isArray(rawData?.items) ? rawData.items : [];

    return {
        status: Boolean(raw?.status),
        data: {
            total: toNumber(rawData?.total),
            current_page: toNumber(rawData?.current_page, 1),
            items: rawItems.map((item: RawObject) => {
                const rawOrderProducts = Array.isArray(item?.order_product)
                    ? item.order_product
                    : (Array.isArray(item?.products) ? item.products : []);

                return {
                    id: toNumber(item?.id),
                    code: String(item?.code ?? ''),
                    customer_id: toNumber(item?.customer_id),
                    fullname: String(item?.fullname ?? ''),
                    phone: String(item?.phone ?? ''),
                    total: toNumber(item?.total),
                    status: toNumber(item?.status),
                    payment_status: toNumber(item?.payment_status),
                    created_at: String(item?.created_at ?? ''),
                    address: String(item?.address ?? ''),
                    order_product: rawOrderProducts.map((productItem: RawObject) => {
                        const product: RawObject = productItem?.product && typeof productItem.product === 'object'
                            ? productItem.product
                            : {};

                        return {
                            id: toNumber(productItem?.id ?? product?.id),
                            order_id: toNumber(productItem?.order_id ?? item?.id),
                            slug_product: String(productItem?.slug_product ?? product?.slug ?? ''),
                            quantity_product: toNumber(productItem?.quantity_product ?? productItem?.quantity),
                            name_product: String(productItem?.name_product ?? product?.name ?? ''),
                            avatar_product: String(productItem?.avatar_product ?? product?.avatar ?? product?.avatar_url ?? ''),
                            price_product: toNumber(productItem?.price_product ?? product?.price),
                            product_id: toNumber(productItem?.product_id ?? product?.id, 0) || undefined,
                        };
                    }),
                };
            }),
        },
        msg: String(raw?.msg ?? ''),
    };
}

async function createOrder(orderData: OrderData) {
    try {
        const formData = new FormData();
        for (const key in orderData) {
            if (key === 'list_products') {
                formData.append(key, JSON.stringify(orderData[key as keyof OrderData]));
            } else {
                formData.append(key, String(orderData[key as keyof OrderData]));
            }
        }
        formData.append('total', orderData.list_products.reduce((sum, item) => sum + item.price * item.quantity, 0).toString());
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/order/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        console.log('Order created successfully', data);
        return data as OrderResponse;
    } catch (error) {
        console.error('Failed to create order:', error);
        return {
            order_id: '',
            status: false,
            msg: 'Failed to create order',
        };
    }
}

async function createOrderV3(orderData: OrderV3Data): Promise<OrderV3Response | {status: boolean; error: any}> {
    try {
        const payload: OrderV3Data = {
            ...orderData,
            list_products: orderData.list_products,
        };

        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/order/create_v3`, {
            method: 'POST',
            headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        return data as OrderV3Response;
    } catch (error) {
        console.error('Failed to create order v3:', error);
        toast.error('Tạo đơn hàng thất bại. Vui lòng thử lại.', {
            position: "bottom-right",
        });
        return {
            status: false,
            msg: 'Failed to create order v3',
        };
    }
}

async function getOrderList(
    page: number, 
    limit: number, 
    sort?: string, 
    status?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
    productId?: string
) : Promise<OrderListResponse | null> {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        if (sort) {
            params.append('sort[]=', sort);
        }
        if (status !== undefined) {
            params.append('status', status.toString());
        }
        if (search) params.append('name', search);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (productId) params.append('product_id', productId);

        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/orders?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return normalizeOrderListResponse(data as RawObject);
    } catch (error) {
        console.error('Failed to fetch order list:', error);
        return null;
    }
}

async function getOrderDetail(orderId: string): Promise<OrderDetailResponse | null> {
    try {
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/order_v3/${orderId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data
    } catch (error) {
        console.error('Failed to fetch order detail:', error);
        return null;
    }
}

interface ProductOrderedHistoryResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: {
            id: number;
            name: string;
            slug: string;
            category_name: string;
            price: number;
            current_price: number;
            discount_price: number;
            purchased_quantity: number;
            avatar: string;
            is_stock: number;
            product_category_id: number;
            promotion: {
                type: string;
                percent: number;
                detail: {
                    id: number;
                    name: string;
                    slug: string;
                    describe: string;
                } | null;
            } | null;
        }[];
    };
    msg?: string;
}

async function productOrderedHistory(page: number, limit: number): Promise<ProductOrderedHistoryResponse | null> {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/api/v1/fe/purchased_products?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data as ProductOrderedHistoryResponse;
    }
    catch (error) {
        console.error('Failed to fetch product ordered history:', error);
        return null;
    }
}


export {
    createOrder,
    createOrderV3,
    getOrderList,
    getOrderDetail,
    productOrderedHistory,
}